import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { type ComboboxOption } from "../combobox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";

export interface ComboboxAdapterProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  renderTrigger?: (selectedOption?: ComboboxOption) => React.ReactNode;
  renderOption?: (option: ComboboxOption) => React.ReactNode;
  children?: React.ReactNode; // For custom content
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  matchTriggerWidth?: boolean;
}

export const ComboboxAdapter = React.forwardRef<
  HTMLDivElement,
  ComboboxAdapterProps
>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select option...",
      searchPlaceholder = "Search...",
      emptyText = "No option found.",
      disabled = false,
      className,
      triggerClassName,
      contentClassName,
      renderTrigger,
      renderOption,
      children,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
      matchTriggerWidth = true,
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement | HTMLDivElement>(null);
    const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>(
      undefined
    );

    React.useImperativeHandle(
      ref,
      () => containerRef.current as HTMLDivElement
    );

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled
      ? (controlledOnOpenChange ?? (() => {}))
      : setInternalOpen;

    // Calculate trigger width when open
    React.useEffect(() => {
      if (open && matchTriggerWidth && triggerRef.current) {
        const width = triggerRef.current.offsetWidth;
        setTriggerWidth(width);
      }
    }, [open, matchTriggerWidth]);

    const selectedOption = React.useMemo(
      () => options.find((option) => option.value === value),
      [options, value]
    );

    return (
      <div ref={containerRef} className="w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            {renderTrigger ? (
              <div
                ref={
                  matchTriggerWidth
                    ? (triggerRef as React.RefObject<HTMLDivElement>)
                    : undefined
                }
                className={cn("cursor-pointer w-full", triggerClassName)}
              >
                {renderTrigger(selectedOption)}
              </div>
            ) : (
              <button
                ref={triggerRef as React.RefObject<HTMLButtonElement>}
                type="button"
                role="combobox"
                aria-expanded={open}
                disabled={disabled}
                className={cn(
                  "flex h-12 w-full items-center justify-between rounded-xl border-1.5 border-gray-300 bg-background-light px-4 py-3 text-left text-sm",
                  !selectedOption && "text-muted-light",
                  disabled && "cursor-not-allowed opacity-50",
                  triggerClassName
                )}
              >
                {selectedOption ? selectedOption.label : placeholder}
                <svg
                  className="ml-2 h-4 w-4 shrink-0 opacity-50"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m7 15 5 5 5-5" />
                  <path d="m7 9 5-5 5 5" />
                </svg>
              </button>
            )}
          </PopoverTrigger>
          <PopoverContent
            className={cn("p-0", contentClassName)}
            align="start"
            matchTriggerWidth={matchTriggerWidth}
            style={
              matchTriggerWidth && triggerWidth
                ? { width: `${triggerWidth}px` }
                : undefined
            }
          >
            {children ?? (
              <Command className={className}>
                <CommandInput placeholder={searchPlaceholder} />
                <CommandList>
                  <CommandEmpty>{emptyText}</CommandEmpty>
                  <CommandGroup>
                    {options.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={(currentValue) => {
                          onValueChange?.(
                            currentValue === value ? "" : currentValue
                          );
                          setOpen(false);
                        }}
                      >
                        {renderOption ? (
                          renderOption(option)
                        ) : (
                          <>
                            {option.icon && (
                              <span className="mr-2">{option.icon}</span>
                            )}
                            {option.label}
                            <Check
                              className={cn(
                                "ml-auto size-4",
                                value === option.value
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);
ComboboxAdapter.displayName = "ComboboxAdapter";
